"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Card, CardBody, Spinner } from "reactstrap"
import DataTable from "react-data-table-component"
import Swal from "sweetalert2"
import { Btn } from "../../../AbstractElements"
import ExhibitionTypeService from "../../../Service/ExhibitionTypeService"
import type { ExhibitionType } from "./types"

interface Props {
  onAdd: () => void
  onEdit: (item: ExhibitionType) => void
  refresh: boolean
}

const ExhibitionTypeList: React.FC<Props> = ({ onAdd, onEdit, refresh }) => {
  const [data, setData] = useState<ExhibitionType[]>([])
  const [loading, setLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await ExhibitionTypeService.getAll()
      setData(response.results)
    } catch (err) {
      console.error("Failed to fetch Exhibition Types:", err)
      await Swal.fire({
        title: "Error!",
        text: "Failed to fetch Exhibition Types. Please try again.",
        icon: "error",
        confirmButtonColor: "#dc3545",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [refresh])

  const handleDelete = async (item: ExhibitionType) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete "${item.name}"? This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      setDeleteLoading(item.id)
      await ExhibitionTypeService.delete(item.id)

      await Swal.fire({
        title: "Deleted!",
        text: `"${item.name}" has been deleted successfully.`,
        icon: "success",
        confirmButtonColor: "#28a745",
      })

      fetchData() // Refresh the list
    } catch (error: any) {
      console.error("Delete error:", error)

      let errorMessage = "Failed to delete Exhibition Type. Please try again."
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      }

      await Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#dc3545",
      })
    } finally {
      setDeleteLoading(null)
    }
  }

  const columns = [
    {
      name: "Name",
      selector: (row: ExhibitionType) => row.name,
      sortable: true,
      grow: 2,
    },
    {
      name: "Actions",
      cell: (row: ExhibitionType) => (
        <div className="d-flex gap-1">
          <Btn
            color="info"
            size="xs"
            onClick={() => onEdit(row)}
            disabled={deleteLoading === row.id}
            title="Edit Exhibition Type"
          >
            Edit
          </Btn>
          <Btn
            color="danger"
            size="xs"
            onClick={() => handleDelete(row)}
            disabled={deleteLoading !== null}
            title="Delete Exhibition Type"
          >
            {deleteLoading === row.id ? <Spinner size="sm" /> : "Delete"}
          </Btn>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ]

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Exhibition Types</h5>
          <Btn color="primary" onClick={onAdd} disabled={loading}>
            Add Exhibition Type
          </Btn>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Loading Exhibition Types...</p>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={data}
            pagination
            highlightOnHover
            striped
            responsive
            noDataComponent={
              <div className="text-center py-4">
                <p>No Exhibition Types found</p>
                <Btn color="primary" size="sm" onClick={onAdd}>
                  Add First Exhibition Type
                </Btn>
              </div>
            }
          />
        )}
      </CardBody>
    </Card>
  )
}

export default ExhibitionTypeList
